import { CdkConnectedOverlay } from "@angular/cdk/overlay";
import { Component, ElementRef, EventEmitter, HostListener, OnDestroy, OnInit, Output, ViewChild } from "@angular/core";
import { MatMenuTrigger } from "@angular/material/menu";
import { Select } from "@ngxs/store";
import { Observable, Subscription } from "rxjs";
import { AppState } from "src/app/app.state";
import { i18nAdapter } from "src/app/i18next/get-translate-and-format";
import { Asset } from "src/app/models/core-api.models";
import { Inspector } from "src/app/models/inspector.model";
import { EvirDivision } from "src/app/models/shared.model";
import { GlobalApiCallsService } from "src/app/services/global-api-calls.service";
import { searchCleanup } from "src/utils/searchCleanup/searchCleanup";
import { FilterCardComponent } from "../filter-card/filter-card.component";
import { OverlayService } from "../overlay/service/overlay.service";
import { SearchInputComponent } from "../search-input/search-input.component";
import { Tab } from "../simple-tabs/simple-tabs.component";
import { getDisplayValue } from "./helpers/get-display-value";
import { getId } from "./helpers/get-id";
import { searchFunctions } from "./search-functions";
export interface TableRecord {
	fields: Array<TableRecordField>;
}

export interface TableRecordField {
	name: string;
	value: string;
}

export const TableName = {
	assets: "getAssetsLoader",
	divisions: "getDivisionsLoader",
	inspectors: "getInspectorsLoader",
} as const;

export type TableName = keyof typeof TableName;
export interface SearchTable {
	tableName: TableName;
	tableQueryName: string;
	displayTableName: string;
	fieldNames: ReadonlyArray<string>;
	displayFieldNames: ReadonlyArray<string>;
}

export interface TableFilterChip {
	displayValue: string;
	record: TableRecord;
	tableDisplayValue: string;
	tableName: string;
	tableQueryName: string;
}

enum State {
	Init,
	Searching,
	SearchCompleted,
}

export interface ViewRecord {
	record: TableRecord | null;
	displayValue: string;
}

export class AssetItem {
	assetId: string;
	assetName: string;
	assetType: string;
	assetDivisions: string[];
	assetJurisdiction: string;
}
const schemas = {
	assets: [{ name: "assetId", _id: "id" }, { name: "divisions" }, { name: "name" }],
	divisions: [{ name: "divisionId", _id: "id" }, { name: "name" }, { name: "type" }],
	inspectors: [{ name: "inspectorId" }, { name: "inspectorFirstName" }, { name: "inspectorLastName" }],
} as const;

const toRecord = (data: Record<string, string>, tableName: keyof typeof schemas) =>
	schemas[tableName].map((field: { name: string; _id?: string }) => ({
		name: field.name,
		value: data[field._id ?? field.name],
	}));

@Component({
	selector: "app-get-table-filter",
	templateUrl: "./get-table-filter.component.html",
	styleUrls: ["./get-table-filter.component.scss"],
})
export class GetTableFilterComponent implements OnInit, OnDestroy {
	@ViewChild("menuTrigger") menuTrigger: MatMenuTrigger;
	@ViewChild(CdkConnectedOverlay) cdkOverlay: CdkConnectedOverlay;
	@ViewChild(SearchInputComponent, { static: true }) searchInputRef: SearchInputComponent;

	@Output() chip = new EventEmitter<TableFilterChip>();

	@Select(AppState.getSearchTables) searchTables$: Observable<Array<SearchTable>>;
	@Select(AppState.selectAssets) assets$: Observable<Asset[]>;
	@Select(AppState.selectDivisions) divisions$: Observable<EvirDivision[]>;
	@Select(AppState.selectUserHasAllDivisions) userHasAllDivisions$: Observable<boolean>;

	@Select(AppState.selectInspectors) inspectors$: Observable<Inspector[]>;
	@Select(AppState.getSelectedCompanyId) selectedCompanyId$: Observable<string>;

	tabs: Array<Tab> = [];
	activeTab: Tab;
	showOverlayContainer = false;
	private suppressOverlayOpen = false;
	pattern = "";
	resetInput = false;
	subscriptions = new Subscription();
	searchState = State.Init;
	State = State;
	isBusy = false;
	companyId: string;
	divisions: EvirDivision[] = [];
	userHasAllDivisions = false;

	viewRecords: Array<ViewRecord> = [];
	placeholder = i18nAdapter.getTranslateAndFormat("search to filter", "capitalize");
	dictionary: Readonly<{
		assets: ReadonlyArray<AssetItem>;
		divisions: EvirDivision[];
		inspectors: Inspector[];
	}> = {
		assets: [],
		divisions: [],
		inspectors: [],
	};

	constructor(
		// private localStoreService: LocalStoreService,
		private filterCard: FilterCardComponent,
		private _eref: ElementRef,
		private overlayService: OverlayService,
		private globalApiCallsService: GlobalApiCallsService,
	) {}

	ngOnDestroy(): void {
		this.subscriptions.unsubscribe();
	}

	ngOnInit() {
		// get the companyId
		this.subscriptions.add(
			this.selectedCompanyId$.subscribe(companyId => {
				this.companyId = companyId;
			}),
		);
		// user has all divisions?
		this.subscriptions.add(
			this.userHasAllDivisions$.subscribe(userHasAllDivisions => {
				this.userHasAllDivisions = userHasAllDivisions;
			}),
		);
		// get the divisions
		this.subscriptions.add(
			this.divisions$.subscribe(divisions => {
				this.divisions = divisions;
			}),
		);
		// listen for busy
		this.subscriptions.add(
			this.overlayService.isBusy$.subscribe(isBusy => {
				this.isBusy = isBusy;
			}),
		);
		// build the tabs
		this.tabs = [
			{
				id: "assets",
				label: "Assets",
				defaultActive: true,
				data: {
					tableName: "assets",
					tableQueryName: "assets",
					displayTableName: "Assets",
				} as any,
			},
			{
				id: "inspectors",
				label: "Inspectors",
				defaultActive: false,
				data: {
					tableName: "inspectors",
					tableQueryName: "inspectors",
					displayTableName: "Inspectors",
				} as any,
			},
			{
				id: "divisions",
				label: "Divisions",
				defaultActive: false,
				data: {
					tableName: "divisions",
					tableQueryName: "divisions",
					displayTableName: "Divisions",
				} as any,
			},
		];
		this.activeTab = this.tabs.find(tab => tab.defaultActive);
	}

	onSearchInputChanged(pattern: string) {
		this.pattern = pattern;

		if (pattern) {
			this.showOverlayContainer = true;
		}

		this.search();
	}

	onTabClicked(tab: Tab) {
		this.activeTab.defaultActive = false;
		tab.defaultActive = true;
		this.activeTab = tab;
		this.cdkOverlay?.overlayRef?.overlayElement?.querySelector<HTMLElement>(".virtual-scroll")?.scrollTo(0, 0);
		this.viewRecords = [];
		this.resetInput = true;
	}

	async search() {
		if (!this.pattern) {
			this.viewRecords = [];
			return;
		}
		this.searchState = State.Searching;

		const records = await searchFunctions[this.activeTab.id](
			this.pattern,
			this.companyId,
			this.divisions,
			this.userHasAllDivisions,
			this.globalApiCallsService,
		);

		const chips = new Set(
			this.filterCard.tableFilterChips?.map(
				({ record: { fields } }) => fields.find(field => field.name.endsWith("Id")).value,
			),
		);

		this.viewRecords = records
			?.map(data => {
				const displayValue = getDisplayValue(data, this.activeTab.data.tableName).trim();
				const displayId = getId(data, this.activeTab.data.tableName).trim();

				return {
					displayValue,
					record: { fields: toRecord(data, this.activeTab.data.tableName) },
					selected: chips.has(displayId),
				};
			})
			.filter(
				({ displayValue }) =>
					true || this.pattern === "" || searchCleanup(displayValue).includes(searchCleanup(this.pattern)),
			)
			.sort((a, b) => a.displayValue.localeCompare(b.displayValue));

		this.searchState = State.SearchCompleted;
	}

	onSearchAreaKeydown(event: KeyboardEvent): void {
		if (event.key !== "Tab" || !this.showOverlayContainer) return;

		const activeEl = document.activeElement;
		const hostEl = this._eref.nativeElement as HTMLElement;
		const isInsideHost = hostEl.contains(activeEl);

		// When focus is on the search input area (inside host, not in overlay):
		// - Tab forward should enter the overlay
		// - Shift+Tab should close the overlay and let focus leave naturally
		if (isInsideHost) {
			if (!event.shiftKey) {
				const isClearBtnFocused = activeEl?.getAttribute("data-testid") === "clear-btn";
				if (isClearBtnFocused || !this.searchInputRef?.displayClearBtn) {
					event.preventDefault();
					this.focusFirstTab();
				}
			} else {
				this.showOverlayContainer = false;
			}
		}
	}

	onClickHighlightedText(viewRecord: ViewRecord) {
		this.showOverlayContainer = false;
		this.resetInput = true;

		// Restore focus to search input without reopening the overlay
		this.suppressOverlayOpen = true;
		setTimeout(() => {
			this.focusSearchInput();
			setTimeout(() => {
				this.suppressOverlayOpen = false;
			});
		});

		this.chip.emit({
			displayValue: viewRecord.displayValue,
			record: viewRecord.record,
			tableDisplayValue: this.activeTab.data.displayTableName,
			tableName: this.activeTab.data.tableName,
			tableQueryName: this.activeTab.data.tableQueryName,
		});
	}

	onHasFocus() {
		this.resetInput = false;

		if (!this.suppressOverlayOpen) {
			this.showOverlayContainer = true;
		}
		this.suppressOverlayOpen = false;

		this.search();
	}

	@HostListener("document:keydown", ["$event"])
	handleDocumentKeydown(event: KeyboardEvent): void {
		if (event.key === "Escape") {
			if (this.showOverlayContainer) {
				event.preventDefault();
				this.suppressOverlayOpen = true;
				this.showOverlayContainer = false;
				this.focusSearchInput();
				setTimeout(() => {
					this.suppressOverlayOpen = false;
				});
			}
			return;
		}
	}

	onFirstTabShiftTab(): void {
		const clearBtnEl = (this._eref.nativeElement as HTMLElement).querySelector<HTMLElement>(
			"[data-testid='clear-btn']",
		);
		if (clearBtnEl) {
			clearBtnEl.focus();
		} else {
			this.focusSearchInput();
		}
	}

	onLastTabTab(): void {
		if (this.viewRecords.length > 0) {
			this.scrollToRecordAndFocus(0);
		} else {
			// No records: cycle focus back to search input
			this.focusSearchInput();
		}
	}

	onRecordKeydown(event: KeyboardEvent, viewRecord: ViewRecord): void {
		if (event.key === "Enter" || event.key === " ") {
			event.preventDefault();
			this.onClickHighlightedText(viewRecord);
			return;
		}
		if (event.key === "ArrowDown" || event.key === "ArrowUp") {
			event.preventDefault();
			const currentIndex = this.viewRecords.indexOf(viewRecord);
			if (event.key === "ArrowDown") {
				if (currentIndex < this.viewRecords.length - 1) {
					this.scrollToRecordAndFocus(currentIndex + 1);
				}
			} else {
				if (currentIndex > 0) {
					this.scrollToRecordAndFocus(currentIndex - 1);
				} else {
					this.focusLastTab();
				}
			}
			return;
		}
		if (event.key === "PageDown" || event.key === "PageUp") {
			event.preventDefault();
			const currentIndex = this.viewRecords.indexOf(viewRecord);
			const scrollContainer =
				this.cdkOverlay?.overlayRef?.overlayElement?.querySelector<HTMLElement>(".virtual-scroll");
			const pageSize = scrollContainer ? Math.floor(scrollContainer.clientHeight / 36) : 5;
			const targetIndex =
				event.key === "PageDown"
					? Math.min(currentIndex + pageSize, this.viewRecords.length - 1)
					: Math.max(currentIndex - pageSize, 0);
			this.scrollToRecordAndFocus(targetIndex);
			return;
		}

		if (event.key !== "Tab") return;

		event.preventDefault();
		const currentIndex = this.viewRecords.indexOf(viewRecord);
		if (event.shiftKey) {
			if (currentIndex <= 0) {
				this.focusLastTab();
			} else {
				this.scrollToRecordAndFocus(currentIndex - 1);
			}
		} else {
			if (currentIndex >= this.viewRecords.length - 1) {
				// Last record: cycle focus back to search input
				this.focusSearchInput();
			} else {
				this.scrollToRecordAndFocus(currentIndex + 1);
			}
		}
	}

	scrollRecordIntoView(event: FocusEvent): void {
		const target = event.target as HTMLElement;
		const scrollContainer =
			this.cdkOverlay?.overlayRef?.overlayElement?.querySelector<HTMLElement>(".virtual-scroll");
		if (!scrollContainer) return;
		const containerRect = scrollContainer.getBoundingClientRect();
		const targetRect = target.getBoundingClientRect();
		if (targetRect.bottom > containerRect.bottom) {
			scrollContainer.scrollTop += targetRect.bottom - containerRect.bottom;
		} else if (targetRect.top < containerRect.top) {
			scrollContainer.scrollTop -= containerRect.top - targetRect.top;
		}
	}

	private scrollToRecordAndFocus(index: number): void {
		const scrollContainer =
			this.cdkOverlay?.overlayRef?.overlayElement?.querySelector<HTMLElement>(".virtual-scroll");
		if (!scrollContainer) return;

		const itemSize = 36;
		const containerHeight = scrollContainer.clientHeight;
		const targetTop = index * itemSize;

		// Scroll so the target item falls within the visible area
		if (targetTop < scrollContainer.scrollTop) {
			scrollContainer.scrollTop = targetTop;
		} else if (targetTop + itemSize > scrollContainer.scrollTop + containerHeight) {
			scrollContainer.scrollTop = targetTop + itemSize - containerHeight;
		}

		// After CDK reacts to the scroll event and renders the item, focus it by index
		setTimeout(() => {
			const target = this.cdkOverlay?.overlayRef?.overlayElement?.querySelector<HTMLElement>(
				`[data-record-index="${index}"]`,
			);
			target?.focus();
		});
	}

	private focusSearchInput(): void {
		this.searchInputRef?.input?.nativeElement.focus();
	}

	private focusFirstTab(): void {
		setTimeout(() => {
			const activeTab = this.cdkOverlay?.overlayRef?.overlayElement?.querySelector<HTMLElement>('.tab[tabindex="0"]');
			if (activeTab) {
				activeTab.focus();
			} else {
				this.cdkOverlay?.overlayRef?.overlayElement?.querySelector<HTMLElement>(".tab")?.focus();
			}
		});
	}

	private focusLastTab(): void {
		setTimeout(() => {
			const activeTab = this.cdkOverlay?.overlayRef?.overlayElement?.querySelector<HTMLElement>('.tab[tabindex="0"]');
			if (activeTab) {
				activeTab.focus();
			} else {
				const tabs = this.cdkOverlay?.overlayRef?.overlayElement?.querySelectorAll<HTMLElement>(".tab");
				tabs?.[tabs.length - 1]?.focus();
			}
		});
	}

	@HostListener("window:click", ["$event"])
	clickOutsideOverlay(event: MouseEvent) {
		if (!this._eref.nativeElement.contains(event.target)) {
			this.showOverlayContainer = false;

			this.resetInput = true;
		}
	}

	clearInput() {
		this.resetInput = true;
	}
}
